<script>
import ClickOutside from 'vue-click-outside'
import BottomSheet from '../bottom-sheet.vue'

const model = {
  prop: 'selected',
  event: 'change'
}
export default {
  model,
  components: {
    BottomSheet,
  },
  directives: {
    ClickOutside,
  },
  props: {
    selected: {
      type: [String, Object]
    },
    options: {
      type: Array,
      default: () => [{ label: 'Option 1', value: 1 }, { label: 'Option 2', value: 2 }],
      validator: function(options) {
        const optionsHasValidProperties = options
          .map(opt => Object.prototype.hasOwnProperty.call(opt, 'label') && Object.prototype.hasOwnProperty.call(opt, 'value'))
          .reduce((value, next) => { if (!next) value = next; return value; }, true)

        return optionsHasValidProperties
      }
    },
    clearable: Boolean,
    disabled: {
      type: Boolean,
    },
    readonly: {
      type: Boolean,
    },
    required: {
      type: Boolean,
    },
    placeholder: {
      type: String,
      default: () => 'Please select option'
    },
    name: String,
    label: {
      type: String,
    },
    rules: {
      type: String,
    },
    useBottomSheet: Boolean,
    bottomSheetTitle: String,
    bottomSheetClass: String,
    customClassOptions: String,
  },
  data() {
    return {
      open: false,
    }
  },
  computed: {
    displayedValue() {
      return this.selected || this.placeholder
    },
    value: {
      get() {
        return this.selected
      },
      set(value) {
        this.$emit('change', value.value)
      },
    },
  },
  methods: {
    select(item) {
      this.$emit('change', item.value)
      this.open = false
    },
    clearSelection(e) {
      e.stopPropagation()
      this.$emit('change', undefined) 
    },
    close() {
      if (!this.useBottomSheet) {
        this.open = false
      }
    }
  },
  watch: {
    open(open) {
      if (!open) {
        // fix race condition
        setTimeout(() => {
          this.$refs.dropdown && this.$refs.dropdown.validate()
        }, 10)
        this.$emit('closed')
        if (this.useBottomSheet) this.$refs.bottomsheet.close()
      }
      if (open) {
        if (this.useBottomSheet) this.$refs.bottomsheet.open()
        this.$emit('opened')
      }
    }
  }
}
</script>

<template>
  <ValidationProvider ref="dropdown" :rules="rules" v-slot="{ errors, passed, failed }">
    <div v-click-outside="close" class="classic-form__dropdown">
      <label v-if="label">
        {{ label }}&nbsp;
        <svg v-if="required" width="7" height="6" viewBox="0 0 7 6" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M2.71733 5.86375L2.81179 3.76574L1.0419 4.90423L0.5 3.95963L2.37926 3.00011L0.5 2.04059L1.0419 1.09599L2.81179 2.23449L2.71733 0.136475H3.79616L3.7017 2.23449L5.47159 1.09599L6.01349 2.04059L4.13423 3.00011L6.01349 3.95963L5.47159 4.90423L3.7017 3.76574L3.79616 5.86375H2.71733Z"
            fill="#E12121" />
        </svg>
      </label>
      <input class="classic-form__dropdown__input-hide" type="text" :name="name" :value="value ? value : ''" />
      <div class="classic-form__dropdown__trigger-wrapper">
        <button
          @click="open = !open"
          :data-state="open ? 'open' : 'close'"
          :data-failed="failed"
          class="classic-form__dropdown__trigger"
          :data-active="Boolean(value)" :data-clearable="clearable" :disabled="disabled"
        >
          <slot name="trigger" :displayedValue="displayedValue">
            {{ displayedValue }}
          </slot>
          <span @click="clearSelection" role="button" class="classic-form__dropdown__trigger__close" v-if="clearable && Boolean(value)" />
        </button>
        <div v-if="!useBottomSheet" class="classic-form__dropdown__options" :class="customClassOptions" v-show="open">
          <slot name="before-items"></slot>
          <ul class="">
            <li v-for="item in options" :key="item.value" :data-selected="value && item.value === value" @click="!item.disabled && select(item)" :data-disabled="item.disabled" class="classic-form__dropdown__options__item">
              <slot name="item" :item="item">
                <span>{{ item.label }}</span>
              </slot>
            </li>
          </ul>
          <slot name="after-items"></slot>
        </div>
      </div>
      <span v-if="errors[0]" class="classic-form__dropdown__error-message scroll-to-error-message">{{ errors[0] || '' }}</span>
    </div>
    <BottomSheet v-if="useBottomSheet" ref="bottomsheet" :title="bottomSheetTitle || label" @close="open = false" :custom-class="['bottom-sheet-dropdown',bottomSheetClass || ''].join(' ').trim()">
      <div class="classic-form__dropdown__options classic-form__dropdown__options--bottom-sheet">
        <slot name="before-items"></slot>
        <ul class="">
          <li v-for="item in options" :key="item.value" :data-selected="value && item.value === value" @click="select(item)" class="classic-form__dropdown__options__item">
            <slot name="item" :item="item">
              <span>{{ item.label }}</span>
            </slot>
          </li>
        </ul>
        <slot name="after-items"></slot>
      </div>
    </BottomSheet>
  </ValidationProvider>
</template>

<style lang="scss" scoped>
.classic-form__dropdown {
  position: relative;
  display: inline-block;
  min-width: 210px;
  width: 100%;

  input.classic-form__dropdown__input-hide {
    position: absolute;
    left: 0;
    top: 0;
    border: none !important;
    padding: 0 !important;
    margin: 0 !important;
    width: 100% !important;
    height: 100% !important;
    z-index: -1;
    opacity: 0;
  }

  &__trigger {
    position: relative;
    width: 100%;
    height: 40px;
    padding: 0px 40px 0px 15px;
    background-color: #FDFDFF;
    border-radius: 8px;
    border: none !important;
    outline: 2px solid #DADADA !important;
    outline-offset: -2px !important;
    text-align: inherit;
    cursor: pointer;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;

    &[data-state=open],
    &:focus {
      border: none !important;
      outline-color: #2E47BA !important;
    }

    &[data-failed=true] {
      outline-color: #E12121 !important;
    }

    &__close {
      position: absolute;
      top: 0;
      right: 0;
      display: inline-block;
      width: 40px;
      height: 100%;
      background-image: url("data:image/svg+xml,%3Csvg width='16px' height='16px' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M5 5L15 15M5 15L15 5L5 15Z' stroke='%236C7E95' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A");
      background-repeat: no-repeat;
      background-position: right 12px top 12px;
      z-index: 1;
    }

    &:not([data-active=true]) {
      color: #6C7E95;
    }

    &[data-active=true][data-clearable=true]::after {
      display: none;
    }

    &::after {
      content: '';
      position: absolute;
      right: 0;
      top: 0;
      height: 100%;
      width: 40px;
      background-image: url("data:image/svg+xml,%3csvg width='24px' height='24px' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236C7E95' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.3' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
      background-position: right 8px top 8px;
      background-repeat: no-repeat;
    }

    &[data-state=close]::after {
      transform: rotate(0deg);
    }

    &[data-state=open]::after {
      background-position: right 8px top 6px;
      transform: rotate(180deg);
    }

    &:focus, &:active {
      outline-color: #2E47BA;
    }

    &:disabled {
      cursor: not-allowed;
    }

    &-wrapper {
      position: relative;
      width: 100%;
    }
  }

  &__options {
    z-index: 100;
    position: absolute;
    top: 100%;
    display: flex;
    flex-direction: column;
    padding: 8px 6px;
    background-color: #FFF;
    border-radius: 8px;
    border: 1px solid #E0E9F4;
    box-shadow: 0px 4px 6px -2px #10182808, 0px 12px 16px -4px #10182814;
    list-style: none;
    width: 100%;
    max-height: 256px;
    margin: 8px 0 0;
    overflow-y: auto;
    overflow-x: hidden;

    ul {
      height: 100%;
      overflow-y: auto;
      padding: 0;
      margin: 0;

      &::-webkit-scrollbar {
        width: 10px;
        cursor: pointer;
      }
    
      &::-webkit-scrollbar-thumb {
        background: #20355114;
        opacity: 0.08;
        border-radius: 10px;
        border: 2px solid transparent;
        background-clip: padding-box;
        cursor: pointer;
      }
    
      &::-webkit-scrollbar-thumb:hover {
        background: #20355114;
      }
    }

    &__item {
      width: 100%;
      display: inline-block !important;
      padding: 10px 8px;
      border-radius: 8px;
      cursor: pointer;
      min-height: 40px;
      flex-shrink: 0;

      &[data-selected=true] {
        color:#2E47BA;
        background: #EFF4FB;
      }

      &[data-disabled=true] {
        cursor: not-allowed !important;
      }

      // &[data-selected=true] {
        // background-image: url("data:image/svg+xml,%3Csvg width='16' height='17' viewBox='0 0 16 17' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M3.2002 8.0006L6.89248 11.6929L12.8002 5.78516' stroke='%232E47BA' stroke-width='1.3' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A");
        // background-position: right 10px center;
        // background-repeat: no-repeat;
      // }
    }

    &--bottom-sheet {
      position: relative;
      border-radius: 0;
      border: none;
      margin: 0;
      padding: 0;
      box-shadow: none;
    }
  }

  label {
    display: flex;
    align-items: center;
    color: #203551 !important;
    font-weight: 500 !important;
    font-size: 14px !important;
    line-height: 20px !important;
    letter-spacing: -0.01em !important;
    margin-bottom: 4px !important;
  }

  &__error-message {
    margin-top: 3px;
    display: inline-block;
    font-weight: 400;
    font-size: 10px;
    line-height: 14px;
    color: #E12121;
  }

  .scroll-to-error-message {
    scroll-margin: 84px;
  }
}
</style>

<style lang="scss">
.bottom-sheet-dropdown {
  .classic-form__dropdown__options__item {
    color: #203551;
    padding: 14px 0;
    border-bottom: 0.6px solid #E0E9F4;
    border-radius: 0;

    &[data-selected=true] {
      background-color: transparent;
      background-image: none;
    }
  }
}
</style>